import React, { useMemo, useState, useEffect } from 'react';
import { portfolioRoi, fmtUSD, fmtUSDshort } from '../../data/portfolioRoi';
import { WEB3FORMS_ACCESS_KEY } from '../../config/web3forms';
import { BOOKING_URL } from '../../config/booking';
import { trackEvent } from '../../utils/analytics';
import logo from '../../images/site_logo/buildlab_logo.png';

const MAX_TABLE_ROWS = 80;

/**
 * Branded, print-ready portfolio ROI report generated from the projects currently
 * shown in the pipeline explorer. Decision-maker leave-behind: download/print to PDF,
 * or email a copy (which also routes a lead to BUILDLAB).
 */
const PipelineReport = ({ projects, contextLabel = 'your selected portfolio', onClose }) => {
    // Some CTCAC applications are split across multiple building records, with the
    // unit count recorded on only one of them — the others arrive with null units.
    // Those carry no ROI, so omit them from the report (and note how many).
    const usable = useMemo(() => projects.filter((p) => (p.totalUnits || 0) > 0), [projects]);
    const omitted = projects.length - usable.length;
    const { rows, agg } = useMemo(() => portfolioRoi(usable), [usable]);
    const sortedRows = useMemo(
        () => [...rows].sort((a, b) => b.roi.totalValue - a.roi.totalValue),
        [rows]
    );
    const tableRows = sortedRows.slice(0, MAX_TABLE_ROWS);

    const today = useMemo(
        () => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        []
    );

    // Organization name for the header — pulled from (1) the explicit context label
    // passed by the developer page, (2) the filtered set itself if every project
    // shares the same developer (so a name-search like "BRIDGE" still names the
    // report after the org), or (3) the context label / a generic fallback.
    const orgName = useMemo(() => {
        const m = /^(.*)'s portfolio$/i.exec(contextLabel || '');
        if (m) return m[1];
        const devs = new Set(usable.map((p) => p.developer).filter(Boolean));
        if (devs.size === 1) return [...devs][0];
        if (/^your selected portfolio$/i.test(contextLabel || '')) return 'Affordable Housing';
        return contextLabel;
    }, [contextLabel, usable]);

    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error

    // Lock background scroll while the report is open; Esc closes. Also set a
    // meaningful document title so the printed/saved PDF gets a sensible filename.
    useEffect(() => {
        const prev = document.body.style.overflow;
        const prevTitle = document.title;
        document.body.style.overflow = 'hidden';
        document.title = `${orgName} Pipeline — BUILDLAB ROI Report`;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            document.title = prevTitle;
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose, orgName]);

    const handlePrint = () => {
        trackEvent('report_download', { properties: agg.count, units: agg.units });
        window.print();
    };

    const handleEmail = async (e) => {
        e.preventDefault();
        if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setStatus('error'); return; }
        setStatus('sending');

        const lines = sortedRows.slice(0, 40).map((r, i) =>
            `${i + 1}. ${r.name} — ${r.city || ''}, ${r.county} Co. · ${r.roi.units} units · ${r.creditType || '—'} · door-fee ${fmtUSD(r.roi.doorFee)} · savings ${fmtUSD(r.roi.infra)} · value ${fmtUSD(r.roi.totalValue)}`
        ).join('\n');

        const message =
`BUILDLAB — Affordable Housing Portfolio Technology ROI Summary
Prepared ${today}  ·  Scope: ${contextLabel}

PORTFOLIO TOTALS
  Properties: ${agg.count}
  Total units: ${agg.units.toLocaleString()}
  Counties: ${agg.counties}
  One-time ISP door-fee income: ${fmtUSD(agg.doorFee)}
  Ongoing ISP profit share: ${agg.profitRange} of ISP monthly revenue${agg.tier8 && agg.tier7 ? ` (${agg.tier8} at 8%, ${agg.tier7} at 7%)` : ''}
  Complimentary WiFi value (yr 1): ${fmtUSD(agg.wifi)}
  Infrastructure design & material savings: ${fmtUSD(agg.infra)}
  Total value generated: ${fmtUSD(agg.totalValue)}
  Est. BUILDLAB design & coordination fee: ${fmtUSD(agg.fee)}
  Net value to developer: ${fmtUSD(agg.net)}

PROPERTIES (top ${Math.min(sortedRows.length, 40)} by value)
${lines}

Figures are unit-driven estimates from CTCAC pipeline data — not a quote.
Book a tailored review: ${BOOKING_URL}`;

        try {
            const res = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    access_key: WEB3FORMS_ACCESS_KEY,
                    subject: `Portfolio ROI Report — ${agg.count} properties, ${agg.units.toLocaleString()} units`,
                    from_name: 'BUILDLAB Portfolio ROI Report',
                    email,
                    replyto: email,
                    message,
                }),
            });
            const data = await res.json();
            if (data.success) {
                setStatus('sent');
                trackEvent('report_email', { properties: agg.count, units: agg.units });
            } else { setStatus('error'); }
        } catch (err) { setStatus('error'); }
    };

    const Stat = ({ value, label, accent }) => (
        <div className="pr-stat">
            <div className={`pr-stat-num${accent ? ' ' + accent : ''}`}>{value}</div>
            <div className="pr-stat-lbl">{label}</div>
        </div>
    );

    return (
        <div className="pr-overlay" role="dialog" aria-modal="true" aria-label="Portfolio ROI report">
            {/* Action bar — hidden when printing */}
            <div className="pr-actions">
                <button type="button" className="pr-act pr-act-ghost" onClick={onClose}>
                    <i className="fa-solid fa-arrow-left" aria-hidden="true"></i> Back
                </button>
                <div className="pr-act-right">
                    <form className="pr-email" onSubmit={handleEmail}>
                        <input
                            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.com" aria-label="Your email" disabled={status === 'sent'}
                        />
                        <button type="submit" className="pr-act pr-act-primary" disabled={status === 'sending' || status === 'sent'}>
                            {status === 'sending' ? 'Sending…' : status === 'sent' ? 'Sent ✓' : 'Email me a copy'}
                        </button>
                    </form>
                    <button type="button" className="pr-act pr-act-dark" onClick={handlePrint}>
                        <i className="fa-solid fa-download" aria-hidden="true"></i> Download / Print PDF
                    </button>
                </div>
            </div>
            {status === 'error' && <div className="pr-note pr-note-err">Please enter a valid email and try again.</div>}
            {status === 'sent' && <div className="pr-note pr-note-ok">Sent — a copy is on its way, and the BUILDLAB team will follow up with a tailored proposal.</div>}

            {/* The report "paper" */}
            <div className="pr-paper">
                <header className="pr-head">
                    <div className="pr-head-brand">
                        <img src={logo} alt="BUILDLAB" className="pr-logo" />
                        <div className="pr-head-meta">
                            <span>Prepared {today}</span>
                            <span>Confidential — for the recipient&rsquo;s internal review</span>
                        </div>
                    </div>
                    <div className="pr-head-rule" />
                    <p className="pr-eyebrow">{orgName} Pipeline · BUILDLAB ROI Report</p>
                    <h1 className="pr-title">{orgName} — Pipeline ROI Report</h1>
                    <p className="pr-sub">
                        An estimate of the ISP income, complimentary resident WiFi value, and infrastructure
                        design savings BUILDLAB can deliver
                        {orgName && orgName !== 'Affordable Housing'
                            ? <> for <strong>{orgName}</strong></>
                            : <> across <strong>{contextLabel}</strong></>}
                        {' '}— based on current California LIHTC pipeline data.
                    </p>
                </header>

                <section className="pr-summary">
                    <Stat value={agg.count} label="Properties" />
                    <Stat value={agg.units.toLocaleString()} label="Total units" />
                    <Stat value={agg.counties} label="Counties" />
                    <Stat value={fmtUSDshort(agg.totalValue)} label="Total value generated" accent="gold" />
                    <Stat value={fmtUSDshort(agg.net)} label="Net of BUILDLAB fee" accent="blue" />
                </section>

                <h2 className="pr-h2">Where the value comes from</h2>
                <div className="pr-cards">
                    <div className="pr-card">
                        <div className="pr-card-lbl">One-time ISP door-fee income</div>
                        <div className="pr-card-val gold">{fmtUSD(agg.doorFee)}</div>
                        <div className="pr-card-sub">$250–$300 / unit negotiated via ISP Right-of-Entry</div>
                    </div>
                    <div className="pr-card">
                        <div className="pr-card-lbl">Ongoing ISP profit share</div>
                        <div className="pr-card-val gold">{agg.profitRange}</div>
                        <div className="pr-card-sub">
                            {agg.tier8 && agg.tier7
                                ? `of ISP monthly revenue · ${agg.tier8} at 8% (100+ units), ${agg.tier7} at 7%`
                                : 'of ISP monthly revenue, recurring'}
                        </div>
                    </div>
                    <div className="pr-card">
                        <div className="pr-card-lbl">Complimentary WiFi value (yr 1)</div>
                        <div className="pr-card-val teal">{fmtUSD(agg.wifi)}</div>
                        <div className="pr-card-sub">ISP-provided hardware + 12 months internet</div>
                    </div>
                    <div className="pr-card">
                        <div className="pr-card-lbl">Infrastructure design savings</div>
                        <div className="pr-card-val teal">{fmtUSD(agg.infra)}</div>
                        <div className="pr-card-sub">cabling, DAS/ERCCS &amp; surveillance design review</div>
                    </div>
                    <div className="pr-card">
                        <div className="pr-card-lbl">Total value generated</div>
                        <div className="pr-card-val blue">{fmtUSD(agg.totalValue)}</div>
                        <div className="pr-card-sub">door fees + WiFi value + design savings</div>
                    </div>
                    <div className="pr-card">
                        <div className="pr-card-lbl">Est. BUILDLAB fee</div>
                        <div className="pr-card-val">{fmtUSD(agg.fee)}</div>
                        <div className="pr-card-sub">$310 / unit · design &amp; coordination</div>
                    </div>
                    <div className="pr-card pr-card-roi">
                        <div className="pr-card-lbl">Return on investment</div>
                        <div className="pr-card-val">{agg.roiPct.toLocaleString()}%</div>
                        <div className="pr-card-sub">net value returned per dollar of BUILDLAB fee — {fmtUSD(agg.net)} net on a {fmtUSD(agg.fee)} fee</div>
                    </div>
                </div>

                <h2 className="pr-h2">Property-by-property breakdown</h2>
                <table className="pr-table">
                    <thead>
                        <tr>
                            <th>#</th><th>Property</th><th>Location</th><th className="num">Units</th>
                            <th>Credit</th><th className="num">Door fee</th><th className="num">Design savings</th><th className="num">Total value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map((r, i) => (
                            <tr key={r._uid || `${r.id}-${i}`}>
                                <td>{i + 1}</td>
                                <td className="pr-td-name">{r.name}</td>
                                <td>{[r.city, r.county && `${r.county} Co.`].filter(Boolean).join(', ')}</td>
                                <td className="num">{r.roi.units.toLocaleString()}</td>
                                <td>{r.creditType || '—'}</td>
                                <td className="num">{fmtUSD(r.roi.doorFee)}</td>
                                <td className="num">{fmtUSD(r.roi.infra)}</td>
                                <td className="num strong">{fmtUSD(r.roi.totalValue)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={3}>Portfolio total ({agg.count} {agg.count === 1 ? 'property' : 'properties'})</td>
                            <td className="num">{agg.units.toLocaleString()}</td>
                            <td></td>
                            <td className="num">{fmtUSD(agg.doorFee)}</td>
                            <td className="num">{fmtUSD(agg.infra)}</td>
                            <td className="num strong">{fmtUSD(agg.totalValue)}</td>
                        </tr>
                    </tfoot>
                </table>
                {sortedRows.length > MAX_TABLE_ROWS && (
                    <p className="pr-more">Showing the top {MAX_TABLE_ROWS} properties by value, of {sortedRows.length} total. Totals above reflect the full set.</p>
                )}
                {omitted > 0 && (
                    <p className="pr-more">{omitted} {omitted === 1 ? 'record was' : 'records were'} omitted for having no reported unit count (multi-building CTCAC applications where the units are listed on a sibling record).</p>
                )}

                <section className="pr-cta">
                    <h3>Turn this estimate into a fixed scope</h3>
                    <p>
                        BUILDLAB embeds technology planning from day one — securing your TCAC broadband score,
                        negotiating ISP Right-of-Entry agreements, and engineering low-voltage, surveillance,
                        door access, and fire &amp; life safety in-house. The figures above are a starting model;
                        a 30-minute review turns them into a project-specific scope and budget.
                    </p>
                    <div className="pr-cta-row">
                        <span className="pr-cta-link">Book a review: {BOOKING_URL.replace('https://', '')}</span>
                        <span className="pr-cta-link">info@buildlabconsulting.com · (858) 952-6234</span>
                    </div>
                </section>

                <footer className="pr-foot">
                    <p>
                        <strong>Methodology &amp; disclaimer.</strong> Figures are unit-driven estimates derived from
                        California Tax Credit Allocation Committee (CTCAC) pipeline data and BUILDLAB&rsquo;s standard
                        per-unit models (ISP door fee $250–$300/unit; infrastructure design savings ~$339/unit; fee
                        $310/unit). They are planning estimates, not a quote or a guarantee of results, and depend on
                        final project scope, ISP terms, and design. © {new Date().getFullYear()} BUILDLAB — Technology Consulting &amp; Design.
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default PipelineReport;
